import { StaticImage } from 'gatsby-plugin-image';
import JSConfetti from 'js-confetti';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Wheel, { WheelRef } from './Wheel';
import WinnerDialog from './WinnerDialog';
import { ResetButton, SpinButton, VolumeButton } from './buttons';
import { QueryParamKeys } from './constants';
import { Segment, SpinTime, decodeText, encodeText } from './utils';

const EmbeddedWheelOfNames: React.FC = () => {
  const wheelRef = useRef<WheelRef>(null);
  const jsConfettiRef = useRef<JSConfetti>();

  useEffect(() => {
    if (!jsConfettiRef.current) {
      jsConfettiRef.current = new JSConfetti();
    }
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const iframeWindow = window.self;
        if (iframeWindow) {
          const iframeHeight = iframeWindow.innerHeight;
          const wheelBaseSize = 700; // Assuming this is the base size of your wheel
          const newScale = iframeHeight / wheelBaseSize;
          setScale(newScale);

          // Adjust the container height to match the iframe height
          containerRef.current.style.height = `${iframeHeight}px`;
        }
      }
    };

    handleResize(); // Initial calculation
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [demoSpin, setDemoSpin] = useState(true);
  const [winner, setWinner] = useState<Segment>();
  const [winnerDialogOpen, setWinnerDialogOpen] = useState(false);
  const [volumeOn, setVolumeOn] = useState(true);
  const [spinButtonDisabled, setSpinButtonDisabled] = useState(false);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [spinTime, setSpinTime] = useState<SpinTime>(11);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const contentValue = params.get(QueryParamKeys.content);
    const colorSchemeValue = params.get(QueryParamKeys.colorScheme);
    const spinTimeValue = params.get(QueryParamKeys.spinTime);

    if (contentValue) {
      try {
        const decodedContent = decodeText(contentValue);
        const lines = decodedContent
          .split('\n')
          .filter((line) => line.trim() !== '');
        const colors = colorSchemeValue
          ? JSON.parse(decodeText(colorSchemeValue))
          : [];
        const newSegments = lines.map((label, index) => ({
          label,
          color: colors[index % colors.length],
        }));
        setSegments(newSegments);
      } catch (error) {
        console.error('Failed to decode content or color scheme', error);
      }
    }

    if (spinTimeValue) {
      setSpinTime(parseInt(spinTimeValue) as SpinTime);
    }
  }, []);

  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.setSegments(segments);
      wheelRef.current.setSpinTime(spinTime);
    }
  }, [segments, spinTime]);

  useEffect(() => {
    if (wheelRef.current) {
      wheelRef.current.toggleSound(volumeOn);
    }
  }, [volumeOn]);

  const updateURLParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set(
      QueryParamKeys.content,
      encodeText(segments.map((s) => s.label).join('\n'))
    );
    params.set(
      QueryParamKeys.colorScheme,
      encodeText(JSON.stringify(segments.map((s) => s.color)))
    );
    params.set(QueryParamKeys.spinTime, spinTime.toString());
    window.history.replaceState(null, '', `?${params}`);
  }, [segments, spinTime]);

  const handleWinnerChange = useCallback((segment: Segment | undefined) => {
    setWinner(segment);
    if (!segment) {
      setSpinButtonDisabled(false);
      return;
    }

    setTimeout(() => {
      jsConfettiRef.current?.addConfetti();
    }, 600);
    setTimeout(() => {
      setWinnerDialogOpen(true);
      setSpinButtonDisabled(false);
    }, 800);
  }, []);

  const spin = useCallback(() => {
    setSpinButtonDisabled(true);
    setDemoSpin(false);
    wheelRef.current?.spin();
    setWinner(undefined);
    setWinnerDialogOpen(false);
  }, []);

  const handleWinnerDialogClose = useCallback(() => {
    setWinnerDialogOpen(false);
  }, []);

  const handleRemoveItemAndClose = useCallback(
    (segment: Segment) => {
      const index = segments.findIndex(
        (s) => s.label === segment.label && s.color === segment.color
      );
      if (index !== -1) {
        const newSegments = [...segments];
        newSegments.splice(index, 1);
        setSegments(newSegments);
        updateURLParams();
      }
      setWinnerDialogOpen(false);
    },
    [segments, updateURLParams]
  );

  // New function to handle reset
  const handleReset = useCallback(() => {
    if (window) {
      window.location.reload();
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative flex justify-center items-center w-full h-full bg-transparent overflow-hidden"
    >
      <div
        className="relative"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        <Wheel
          ref={wheelRef}
          onSegmentSelected={handleWinnerChange}
          className="mx-auto"
        />
        <SpinButton
          disabled={spinButtonDisabled}
          pulseAnimation={demoSpin && !spinButtonDisabled}
          onClick={spin}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        />
        <div className="absolute top-2 left-2">
          <a
            target="_blank"
            rel="noreferrer"
            href="https://seatingchartmaker.app"
          >
            <StaticImage
              src="../../images/icon-wheel.png"
              alt="Seating Chart Maker"
              className="rounded-full h-10 w-10"
              placeholder="blurred"
            />
          </a>
        </div>
        <div className="absolute bottom-10 right-4">
          <div className="flex flex-col items-end gap-2">
            <VolumeButton volumeOn={volumeOn} onChange={setVolumeOn} />
            <ResetButton onClick={handleReset} />
          </div>
        </div>
        <WinnerDialog
          isOpen={winnerDialogOpen}
          segment={winner as Segment}
          onClose={handleWinnerDialogClose}
          onRemoveItemAndClose={handleRemoveItemAndClose}
        />
      </div>
    </div>
  );
};

export default EmbeddedWheelOfNames;
